/**
 * Badge « +N XP » qui SURGIT au point de clic d'un indice correct et remonte
 * en fade. Animation CSS pure : translateY(-56px) + opacity 1 → 0 sur 1200ms.
 *
 * Le composant s'auto-démonte après l'animation (le parent gère juste `key`
 * pour re-instancier à chaque bon clic).
 */

import { useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

export interface FloatingXpBadgeProps {
  /** Position VIEWPORT (pas relative). */
  x: number;
  y: number;
  xp: number;
  onDone: () => void;
  /** Durée totale (ms). Défaut 1200. */
  durationMs?: number;
}

export function FloatingXpBadge({ x, y, xp, onDone, durationMs = 1200 }: FloatingXpBadgeProps) {
  useEffect(() => {
    const t = setTimeout(onDone, durationMs + 50);
    return () => clearTimeout(t);
  }, [onDone, durationMs]);

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full"
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <div
        className={cn(
          'clue-xp-anim inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-extrabold shadow-lg',
          'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white',
        )}
        style={{ animationDuration: `${durationMs}ms` }}
      >
        <Sparkles className="h-4 w-4 drop-shadow" />
        <span>+{xp} XP</span>
      </div>
      <style>{`
        @keyframes clue-xp-float {
          0% { transform: translateY(0) scale(0.7); opacity: 0; }
          15% { transform: translateY(-8px) scale(1.1); opacity: 1; }
          30% { transform: translateY(-16px) scale(1); opacity: 1; }
          100% { transform: translateY(-56px) scale(0.95); opacity: 0; }
        }
        .clue-xp-anim {
          animation-name: clue-xp-float;
          animation-timing-function: cubic-bezier(0.22, 0.61, 0.36, 1);
          animation-fill-mode: forwards;
        }
      `}</style>
    </div>
  );
}
